import React, { memo, useEffect, useState } from 'react';
import { formatPhoneNumber } from '@entities/create-segment-modal/lib/form-validator.ts';
import { IFreelanceEmployee } from '@shared/types/types.ts';
import { XmarkIcon } from '@shared/assets/icons';
import { Input, TextField } from '@shared/ui/kit';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles.ts';
import { emailValidator, EMPTY_FIELD_MESSAGE } from '@pages/event/lib/validator.ts';
import { ErrorData } from '@features/api/use-create-user.ts';
import {
  EMAIL_EXISTS_ERROR,
  PHONE_EXISTS_ERROR,
  EMAIL_EXISTS_PATTERN,
  PHONE_EXISTS_PATTERN,
  processEmailError,
  processPhoneError,
  processEmailChangeError,
} from './lib/error-processors.ts';

interface FreelanceEmployeeFormProps {
  employee: IFreelanceEmployee;
  changeEmployee: (value: IFreelanceEmployee) => void;
  onChangeDelete: (freelanceEmployees: IFreelanceEmployee[]) => void;
  freelanceEmployees: IFreelanceEmployee[];
  onEmailError?: (hasError: boolean) => void;
  error?: ErrorData;
  deletable?: boolean;
}

const FreelanceEmployeeForm: React.FC<FreelanceEmployeeFormProps> = memo(
  ({ employee, changeEmployee, onChangeDelete, freelanceEmployees, onEmailError, error, deletable }) => {
    const [errors, setErrors] = useState({
      lastName: '',
      firstName: '',
      email: '',
      phone: '',
    });

    useEffect(() => {
      if (!error) {
        return;
      }

      const hasEmailError = EMAIL_EXISTS_PATTERN.test(error.errorText || '');
      const hasPhoneError = PHONE_EXISTS_PATTERN.test(error.errorText || '');

      setErrors((prevErrors) => ({
        ...prevErrors,
        email: processEmailError(hasEmailError, prevErrors.email),
        phone: processPhoneError(hasPhoneError, prevErrors.phone),
      }));
    }, [error]);

    const handleChangeNumber = (value: string) => {
      const replacedValue = String(value).replace(/\D/g, '');
      let formattedValue = '';

      if (replacedValue.length > 0) {
        // Добавляем логику для первой цифры
        if (replacedValue.startsWith('7') || !replacedValue.startsWith('8')) {
          formattedValue = '8';
        }
        formattedValue += replacedValue;
      }
      // Обрезаем до 11 символов
      formattedValue = formattedValue.slice(0, 11);
      const maskedValue = formatPhoneNumber(formattedValue);

      setErrors((prevErrors) => {
        if (prevErrors.phone !== PHONE_EXISTS_ERROR) {
          return prevErrors;
        }
        return {
          ...prevErrors,
          phone: '',
        };
      });

      onChangeData(maskedValue, 'phone');
    };

    const onChangeData = (value: string, field: keyof typeof errors) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value.trim() ? EMPTY_FIELD_MESSAGE : '',
        email: prevErrors.email === EMAIL_EXISTS_ERROR ? '' : prevErrors.email,
        phone: prevErrors.phone === PHONE_EXISTS_ERROR ? '' : prevErrors.phone,
      }));

      changeEmployee({ ...employee, [field]: value });
    };

    const changeEmailHandler = async (value: string) => {
      const emailError = await emailValidator(value);
      changeEmployee({ ...employee, email: value });

      setErrors((prevErrors) => {
        const newEmailError = processEmailChangeError(emailError, prevErrors.email);
        return {
          ...prevErrors,
          email: newEmailError,
          phone: prevErrors.phone === PHONE_EXISTS_ERROR ? '' : prevErrors.phone,
        };
      });

      onEmailError?.(!!emailError);
    };

    const deleteEmployeeHandler = (employeeId: number) => {
      const filteredEmployee = freelanceEmployees.filter((el: IFreelanceEmployee) => el.id !== employeeId);
      onChangeDelete(filteredEmployee);
    };

    return (
      <div {...stylex.props(styles.container)}>
        <form {...stylex.props(styles.form)}>
          <div {...stylex.props(styles.inputs)}>
            <span {...stylex.props(styles.flex)}>
              <Input
                value={employee.lastName}
                settings={{ width: '100%' }}
                size="small"
                inputMessage={errors.lastName}
                status={errors.lastName ? 'error' : undefined}
                changeFunction={(value: string) => onChangeData(value, 'lastName')}
                placeholder="Введите фамилию участника"
                labelText="Фамилия участника *"
              />
              <Input
                value={employee.firstName}
                settings={{ width: '100%' }}
                size="small"
                inputMessage={errors.firstName}
                status={errors.firstName ? 'error' : undefined}
                changeFunction={(value: string) => onChangeData(value, 'firstName')}
                placeholder="Введите имя участника"
                labelText="Имя участника *"
              />
            </span>

            <span {...stylex.props(styles.flex)}>
              <Input
                value={employee.email}
                settings={{ width: '100%' }}
                size="small"
                changeFunction={async (value: string) => await changeEmailHandler(value)}
                placeholder="Введите email участника"
                labelText="Email *"
                inputMessage={errors.email}
                status={errors.email ? 'error' : undefined}
              />
              <Input
                value={employee.phone}
                settings={{ width: '100%' }}
                size="small"
                inputMessage={errors.phone}
                status={errors.phone ? 'error' : undefined}
                changeFunction={handleChangeNumber}
                placeholder="Введите телефон участника"
                labelText="Телефон *"
              />
            </span>
          </div>
          <TextField
            settings={{
              width: '100%',
              resize: 'none',
            }}
            changeFunction={(e) => changeEmployee({ ...employee, comment: e.target.value })}
            value={employee.comment}
            placeholder="Введите текст"
            labelMsg="Комментарий"
          />
        </form>
        {deletable && typeof employee.id === 'number' ? (
          <XmarkIcon
            onClick={() => {
              if (typeof employee.id === 'number') {
                deleteEmployeeHandler(employee.id);
              }
            }}
            {...stylex.props(styles.xMarkIcon)}
          />
        ) : null}
      </div>
    );
  }
);

export default FreelanceEmployeeForm;
